import qs from 'qs';
import {AUTH_KEY, STATUS} from '@jobseeker/common-core/constants';
import {clearAuth, getAuth} from '@jobseeker/common-core/utils';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';
import {
  getStoredReferrer,
  getStoredLandingUrl,
} from '@jobseeker/common-core/utils/referrer';

import type {AuthType, RegisterType} from './types';

const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_USER_SERVICE}/`
);

const login = async (path: string, authCred: object): Promise<AuthType> => {
  try {
    const response: any = await axios.post(`${path}`, qs.stringify(authCred), {
      withCredentials: true,
      headers: {
        'x-url': getStoredLandingUrl(),
        'x-referer': getStoredReferrer(),
        'x-href': typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    if (response[STATUS] === 200) {
      if (response.data?.twoFactorAuth || response.data?.options?.reset) {
        return response?.data;
      }

      if (response.data?.activationToken) {
        return response?.data;
      }

      return {
        token: response.data?.[AUTH_KEY],
        userId: response.data?.['seekerId'],
        obfuscatedUserId: response.data?.['obfuscatedUserId'],
        expires: 172800,
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const register = async (
  path: string,
  registerInfo: object
): Promise<RegisterType> => {
  try {
    const response = await axios.post(`${path}`, qs.stringify(registerInfo), {
      withCredentials: true,
      headers: {
        'x-url': getStoredLandingUrl(),
        'x-referer': getStoredReferrer(),
        'x-href': typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    if (response[STATUS] === 200 && response.data?.['seekerId']) {
      return {
        token: response.data?.[AUTH_KEY],
        expires: 172800,
        userId: response.data?.['seekerId'],
        emailVerified: false,
      };
    }
    return {
      error: {
        message: 'some issue while registering! Please try again!',
        code: 404,
      },
    };
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const forgotPassword = async (path: string, forgotInfo: object) => {
  try {
    const response = await axios.post(`${path}`, qs.stringify(forgotInfo));

    if (response[STATUS] === 200) {
      if (response.data?.activationToken) {
        return {
          message: response.data.message,
          activationToken: response.data.activationToken,
        };
      }
      return {
        message: response.data['message'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const resetPassword = async (path: string, resetInfo: object) => {
  try {
    const response = await axios.post(`${path}`, qs.stringify(resetInfo));

    if (response[STATUS] === 200) {
      return {
        message: response.data.data['message'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const resendPasswordPolicyEmail = async (path: string, resendInfo: object) => {
  try {
    const response = await axios.post(`${path}`, qs.stringify(resendInfo));

    if (response[STATUS] === 200) {
      return {
        message: response.data['emailSent']
          ? 'Email sent'
          : 'Failed to send email',
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const sendOtp = async (path: string, otpInfo: object) => {
  try {
    const response: any = await axios.post(`${path}`, otpInfo, {
      withCredentials: true,
      headers: {
        'x-url': getStoredLandingUrl(),
        'x-referer': getStoredReferrer(),
        'x-href': typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    if (response[STATUS] === 200) {
      return {
        success: response.data?.success,
        name: response.data?.['name'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const verifyOtp = async (path: string, otpInfo: object): Promise<AuthType> => {
  try {
    const response: any = await axios.post(`${path}`, otpInfo, {
      withCredentials: true,
      headers: {
        'x-url': getStoredLandingUrl(),
        'x-referer': getStoredReferrer(),
        'x-href': typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    if (response[STATUS] === 200) {
      return {
        token: response.data?.[AUTH_KEY],
        userId: response.data?.['seekerId'],
        action: response.data?.['action'],
        name: response.data?.['name'],
        redirectPath: response.data?.['redirectPath'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const sendMobileOtp = async (path: string, otpInfo: object) => {
  try {
    const response: any = await axios.post(`${path}`, otpInfo, {
      withCredentials: true,
      headers: {
        'x-url': getStoredLandingUrl(),
        'x-referer': getStoredReferrer(),
        'x-href': typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    if (response[STATUS] === 200) {
      return {
        success: response.data?.success,
        name: response.data?.['name'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const verifyMobileOtp = async (
  path: string,
  otpInfo: object
): Promise<AuthType> => {
  try {
    const response: any = await axios.post(`${path}`, otpInfo, {
      withCredentials: true,
      headers: {
        'x-url': getStoredLandingUrl(),
        'x-referer': getStoredReferrer(),
        'x-href': typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    if (response[STATUS] === 200) {
      return {
        token: response.data?.[AUTH_KEY],
        sendAuth: response.data?.['sendAuth'],
        action: response.data?.['action'],
        userId: response.data?.['seekerId'],
        seekerId: response.data?.['seekerId'],
        name: response.data?.['name'],
        redirectPath: response.data?.['redirectPath'],
        isNewAccount: response.data?.['isNewAccount'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const logOut = async (path: string, options = {}) => {
  try {
    await axios.post(`${path}`, qs.stringify({}), {
      withCredentials: true,
      ...options,
    });
    clearAuth();
  } catch (error) {
    throw error;
  }
};

const authService = {
  login,
  register,
  forgotPassword,
  logOut,
  sendOtp,
  verifyOtp,
  sendMobileOtp,
  verifyMobileOtp,
  resendPasswordPolicyEmail,
  resetPassword,
};

export default authService;
